export interface ValidationError {
  code: string;
  detail: string;
  attr: string | null;
}

/**
 * Field name mapping for common fields (Vietnamese)
 */
export const FIELD_NAME_MAP: { [key: string]: string } = {
  'password': 'Mật khẩu',
  'confirm_password': 'Xác nhận mật khẩu',
  'email': 'Email',
  'fullName': 'Họ tên',
  'name': 'Tên',
  'code': 'Mã',
  'job_title_id': 'Chức vụ',
  'company_id': 'Công ty',
  'department_id': 'Phòng ban',
  'area_id': 'Khu vực',
  'office_id': 'Văn phòng',
  'position_id': 'Vị trí',
  'userSelectionType': 'Loại user',
  'selectedUserId': 'User đã chọn',
  'username': 'Tên đăng nhập',
  'first_name': 'Tên',
  'last_name': 'Họ',
  'address': 'Địa chỉ',
  'order_code': 'Mã đơn hàng',
  'customer_id': 'Khách hàng',
  'sales_channel_id': 'Kênh bán hàng',
  'sales_employee_id': 'Nhân viên bán hàng',
  'status_id': 'Trạng thái',
};

/**
 * True when API body is a standardized backend error (e.g. drf-standardized-errors).
 */
export function isBackendErrorResponse(data: unknown): data is BackendErrorResponse {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const body = data as BackendErrorResponse;
  return (
    body.type === 'validation_error' ||
    body.type === 'client_error' ||
    (Array.isArray(body.errors) && body.errors.length > 0)
  );
}

/**
 * Normalize RTK rejectWithValue payloads, axios errors, or Error into ApiError.
 */
export function toApiError(error: unknown): ApiError {
  if (isBackendErrorResponse(error)) {
    return { response: { data: error } };
  }

  if (error && typeof error === 'object') {
    const maybeApiError = error as ApiError;
    if (maybeApiError.response?.data) {
      return maybeApiError;
    }
  }

  if (error instanceof Error) {
    try {
      const parsed = JSON.parse(error.message);
      if (isBackendErrorResponse(parsed)) {
        return { response: { data: parsed }, message: error.message };
      }
    } catch {
      // not JSON — use message as-is
    }
    return { message: error.message };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  return {};
}

export interface BackendErrorResponse {
  type?: string;
  errors?: ValidationError[];
  message?: string;
  [key: string]: any; // Allow other field-specific errors
}

export interface ApiError {
  response?: {
    data?: BackendErrorResponse;
    status?: number;
  };
  message?: string;
}

/**
 * Format error for Snackbar display
 * @param error - The error object from API call
 * @param fieldNameMap - Optional map to convert field names to Vietnamese
 * @returns Formatted error message string
 */
export function formatErrorForSnackbar(error: ApiError, fieldNameMap?: { [key: string]: string }): string {
  // Use FIELD_NAME_MAP as default if fieldNameMap not provided
  const effectiveFieldMap = fieldNameMap || FIELD_NAME_MAP;
  
  // Handle validation errors from backend
  if (error.response?.data?.type === 'validation_error' && error.response?.data?.errors) {
    const errorMessages = error.response.data.errors.map((err: ValidationError) => {
      const fieldName = effectiveFieldMap[err.attr || ''] || err.attr;
      return fieldName ? `${fieldName}: ${err.detail}` : err.detail;
    }).join(', ');
    return errorMessages;
  }
  
  // Handle client errors from backend (like authentication errors)
  if (error.response?.data?.type === 'client_error' && error.response?.data?.errors) {
    const errorMessages = error.response.data.errors.map((err: ValidationError) => 
      err.detail
    ).join(', ');
    return errorMessages;
  }
  
  // Handle field-specific errors (like {"password":["Password must be at least 8 characters long."]})
  if (error.response?.data && !error.response.data.type && !error.response.data.message) {
    const errorData = error.response.data;
    const fieldErrors = Object.entries(errorData)
      .filter(([key, value]) => Array.isArray(value) && value.length > 0 && !['type', 'errors', 'message'].includes(key))
      .map(([key, value]) => {
        const fieldName = effectiveFieldMap[key] || key;
        return `${fieldName}: ${(value as string[]).join(', ')}`;
      });
    
    if (fieldErrors.length > 0) {
      return fieldErrors.join('; ');
    }
  }
  
  // Handle other backend errors
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  // Handle network or other errors
  if (error.message) {
    return error.message;
  }
  
  // Default fallback
  return 'An unexpected error occurred';
}

/**
 * Check if error is a validation error
 * @param error - The error object from API call
 * @returns boolean
 */
export function isValidationError(error: ApiError): boolean {
  return error.response?.data?.type === 'validation_error';
}

/**
 * Check if error is a client error
 * @param error - The error object from API call
 * @returns boolean
 */
export function isClientError(error: ApiError): boolean {
  return error.response?.data?.type === 'client_error';
}

/**
 * Get validation errors as array
 * @param error - The error object from API call
 * @returns Array of validation errors or empty array
 */
export function getValidationErrors(error: ApiError): ValidationError[] {
  if (isValidationError(error) && error.response?.data?.errors) {
    return error.response.data.errors;
  }
  return [];
}

/**
 * Get field-specific validation error
 * @param error - The error object from API call
 * @param fieldName - The field name to check
 * @returns Error message for specific field or null
 */
export function getFieldError(error: ApiError, fieldName: string): string | null {
  const validationErrors = getValidationErrors(error);
  const fieldError = validationErrors.find(err => err.attr === fieldName);
  return fieldError ? fieldError.detail : null;
}
